import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Trophy, Volume2, VolumeX } from 'lucide-react';
import { useGameSocket, useSubmitAnswer, useSubmitWordChainWord } from '@/lib/socket/useGameSocket';
import { gameStore } from '@/store/gameStore';
import { useAudio } from '@/lib/audio/useAudio';
import type { PerPlayerResult, LiveScoreboardEntry, WordChainRevealData } from '@/store/gameStore';
import { Avatar } from '@/design-system/components/Avatar';
import { Button } from '@/design-system/components/Button';
import { t } from '@/lib/i18n';
import styles from './PlayPage.module.css';

function useRoundId() {
  const { roundId } = useParams<{ roundId: string }>();
  const parsed = z.string().regex(/^[a-f0-9]{24}$/i).safeParse(roundId);
  if (!parsed.success) throw new Response('Not Found', { status: 404 });
  return parsed.data;
}

function useRemainingMs(serverStartedAt: number, durationSec: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, durationSec * 1000 - (now - serverStartedAt));
}

/** Returns elapsed ms since revealEndsAt - REVEAL_TOTAL_MS */
function useRevealElapsed(revealEndsAt: number | null, totalMs: number) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!revealEndsAt) return;
    const startedAt = revealEndsAt - totalMs;
    const tick = () => setElapsed(Math.min(totalMs, Date.now() - startedAt));
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [revealEndsAt, totalMs]);
  return elapsed;
}

/** Count-up animation hook */
function useCountUp(target: number, durationMs = 1200, startDelay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) { setValue(0); return; }
    let raf: number;
    let startTime: number | null = null;
    const delay = setTimeout(() => {
      const step = (ts: number) => {
        if (!startTime) startTime = ts;
        const progress = Math.min((ts - startTime) / durationMs, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(eased * target));
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, startDelay);
    return () => { clearTimeout(delay); cancelAnimationFrame(raf); };
  }, [target, durationMs, startDelay]);
  return value;
}

const REVEAL_TOTAL_MS = 10_000;
const PHASE2_START_MS = 5_000;

export default function PlayPage() {
  const roundId = useRoundId();
  const navigate = useNavigate();

  useGameSocket(roundId, 'player');
  const submitAnswer = useSubmitAnswer(roundId);

  const phase = gameStore((s) => s.phase);
  const currentQuestion = gameStore((s) => s.currentQuestion);
  const myAnswerSubmitted = gameStore((s) => s.myAnswerSubmitted);
  const myLastResult = gameStore((s) => s.myLastResult);
  const participants = gameStore((s) => s.participants);
  const prevParticipants = gameStore((s) => s.prevParticipants);
  const perPlayerResult = gameStore((s) => s.perPlayerResult);
  const isSpectator = gameStore((s) => s.isSpectator);
  const connectionStatus = gameStore((s) => s.connectionStatus);
  const totalQuestions = gameStore((s) => s.totalQuestions);
  const revealEndsAt = gameStore((s) => s.revealEndsAt);
  const currentExplanation = gameStore((s) => s.currentExplanation);
  const currentCanonicalAnswer = gameStore((s) => s.currentCanonicalAnswer);

  const nextQuestionStartsAt = gameStore((s) => s.nextQuestionStartsAt);
  const nextQuestionOrderIndex = gameStore((s) => s.nextQuestionOrderIndex);

  // Word Chain state
  const wordChainData = gameStore((s) => s.wordChainData);
  const wordChainRevealData = gameStore((s) => s.wordChainRevealData);
  const myParticipantId = gameStore((s) => s.myParticipantId);
  const wordChainWordResults = gameStore((s) => s.wordChainWordResults);
  const wordChainCorrectByWord = gameStore((s) => s.wordChainCorrectByWord);
  const wordChainCurrentWordIndex = gameStore((s) => s.wordChainCurrentWordIndex);
  const wordChainWordPhaseStartedAt = gameStore((s) => s.wordChainWordPhaseStartedAt);
  const wordChainWordPhaseTimeLimitSec = gameStore((s) => s.wordChainWordPhaseTimeLimitSec);
  const wordChainPhaseLeaderboard = gameStore((s) => s.wordChainPhaseLeaderboard);

  const [muted, setMuted] = useState(false);

  // Nhạc gameplay — phát khi đang chơi (question/reveal), tắt khi kết thúc
  useAudio('/audio/gameplay.mp3', {
    enabled: phase === 'question' || phase === 'reveal' || phase === 'transitioning' || phase === 'word_chain',
    volume: muted ? 0 : 0.3,
    fadeOutMs: 1200,
  });

  const [answerText, setAnswerText] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (phase === 'question' && !isSpectator) {
      setAnswerText('');
      setSelectedChoice(null);
      setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 100);
    }
  }, [phase, currentQuestion?.question_id, isSpectator]);

  useEffect(() => {
    if (phase === 'ended') {
      navigate(`/rounds/${roundId}/result`, { replace: true });
    }
  }, [phase, roundId, navigate]);

  const handleChoiceClick = (choice: string) => {
    if (!currentQuestion || myAnswerSubmitted) return;
    setSelectedChoice(choice);
    submitAnswer(currentQuestion.question_id, currentQuestion.order_index, choice);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentQuestion || !answerText.trim() || myAnswerSubmitted) return;
    submitAnswer(currentQuestion.question_id, currentQuestion.order_index, answerText.trim());
  };

  const submitWordChainWord = useSubmitWordChainWord(roundId);

  if (phase === 'waiting' || phase === 'starting') {
    return (
      <div className={styles.waiting}>
        <div className={styles.waitingText}>{t('play.waitingFirstQuestion')}</div>
      </div>
    );
  }

  if (phase === 'word_chain' && !wordChainData) {
    // word_chain_started not yet received (brief window between room_joined and word_chain_started)
    return (
      <div className={styles.root}>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--color-text-secondary)' }}>
          Đang tải trò chơi...
        </div>
      </div>
    );
  }

  if (phase === 'word_chain' && wordChainData) {
    return (
      <WordChainPlayView
        data={wordChainData}
        revealData={wordChainRevealData}
        wordResults={wordChainWordResults}
        correctByWord={wordChainCorrectByWord}
        currentWordIndex={wordChainCurrentWordIndex}
        phaseStartedAt={wordChainWordPhaseStartedAt}
        phaseTimeLimitSec={wordChainWordPhaseTimeLimitSec}
        phaseLeaderboard={wordChainPhaseLeaderboard}
        isSpectator={isSpectator}
        myParticipantId={myParticipantId}
        muted={muted}
        onMuteToggle={() => setMuted((m) => !m)}
        connectionStatus={connectionStatus}
        onSubmitWord={(wordIndex, answer) => submitWordChainWord(wordIndex, answer)}
      />
    );
  }

  if (phase === 'transitioning' && nextQuestionStartsAt) {
    return (
      <TransitionCountdown
        startsAt={nextQuestionStartsAt}
        orderIndex={nextQuestionOrderIndex ?? 1}
        totalQuestions={totalQuestions}
      />
    );
  }

  return (
    <div className={styles.root}>
      {connectionStatus === 'reconnecting' && (
        <div className={styles.connBanner}>{t('common.connecting')}</div>
      )}
      {isSpectator && (
        <div className={styles.spectatorBanner}>{t('play.spectatorBanner')}</div>
      )}

      {currentQuestion && (
        <>
          <div className={styles.header}>
            <div className={styles.progress}>
              {t('play.questionProgress', { n: currentQuestion.order_index, total: totalQuestions > 0 ? totalQuestions : currentQuestion.order_index })}
            </div>
            <div className={styles.headerRight}>
              <button
                className={styles.muteBtn}
                onClick={() => setMuted((m) => !m)}
                title={muted ? t('roundControl.unmuteMusic') : t('roundControl.muteMusic')}
              >
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
              {phase === 'question' && (
                <CountdownTimer
                  serverStartedAt={currentQuestion.server_started_at}
                  durationSec={currentQuestion.time_limit_sec}
                />
              )}
              {phase === 'reveal' && revealEndsAt && (
                <RevealCountdown revealEndsAt={revealEndsAt} totalMs={REVEAL_TOTAL_MS} />
              )}
            </div>
          </div>

          <div className={styles.questionWrap}>
            {currentQuestion.question_type === 'word_scramble' ? (
              <div className={styles.wordScrambleWrap}>
                <p className={styles.wordScrambleInstruction}>{t('play.wordScrambleInstruction')}</p>
                <ScrambledTiles text={currentQuestion.question_text} />
              </div>
            ) : (
              <p className={styles.questionText}>{currentQuestion.question_text}</p>
            )}
          </div>

          {phase === 'question' && !isSpectator && currentQuestion.answer_format === 'multiple_choice' && (
            <div className={styles.answerForm}>
              <div className={styles.choicesGrid}>
                {(currentQuestion.choices ?? []).map((choice, i) => {
                  const isSelected = selectedChoice === choice;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`${styles.choiceBtn} ${isSelected ? styles.choiceBtnSelected : ''}`}
                      onClick={() => handleChoiceClick(choice)}
                      disabled={myAnswerSubmitted}
                    >
                      <span className={styles.choiceLetter}>{String.fromCharCode(65 + i)}</span>
                      <span>{choice}</span>
                    </button>
                  );
                })}
              </div>
              {myAnswerSubmitted && <p className={styles.submittedHint}>{t('play.waitResult')}</p>}
            </div>
          )}

          {phase === 'question' && !isSpectator && currentQuestion.answer_format !== 'multiple_choice' && (
            <form className={styles.answerForm} onSubmit={handleSubmit}>
              <div className={styles.inputRow}>
                <input
                  ref={inputRef}
                  className={`${styles.answerInput} ${myAnswerSubmitted ? styles.answerInputSubmitted : ''}`}
                  placeholder={myAnswerSubmitted ? t('play.submitted') : t('play.answerPlaceholder')}
                  value={myAnswerSubmitted ? '' : answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  disabled={myAnswerSubmitted}
                  autoComplete="off"
                  spellCheck={false}
                />
                <Button type="submit" disabled={myAnswerSubmitted || !answerText.trim()}>
                  {myAnswerSubmitted ? t('play.submitDone') : t('play.submit')}
                </Button>
              </div>
              {myAnswerSubmitted && <p className={styles.submittedHint}>{t('play.waitResult')}</p>}
            </form>
          )}

          {phase === 'question' && isSpectator && (
            <div className={styles.spectatorNote}>{t('play.spectatorNote')}</div>
          )}

          {phase === 'reveal' && revealEndsAt && (
            <RevealPanel
              revealEndsAt={revealEndsAt}
              totalMs={REVEAL_TOTAL_MS}
              phase2StartMs={PHASE2_START_MS}
              perPlayerResult={perPlayerResult}
              participants={participants}
              prevParticipants={prevParticipants}
              myLastResult={myLastResult}
              isSpectator={isSpectator}
              canonicalAnswer={currentCanonicalAnswer ?? ''}
              explanation={currentExplanation}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---- Sub-components ----

function CountdownTimer({ serverStartedAt, durationSec }: { serverStartedAt: number; durationSec: number }) {
  const remainingMs = useRemainingMs(serverStartedAt, durationSec);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const pct = remainingMs / (durationSec * 1000);
  const urgent = remainingSec <= 5;
  return (
    <div className={styles.timerWrap}>
      <div className={`${styles.timerNum} ${urgent ? styles.timerUrgent : ''}`}>{remainingSec}</div>
      <div className={styles.timerBar}>
        <div className={`${styles.timerFill} ${urgent ? styles.timerFillUrgent : ''}`} style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

function RevealCountdown({ revealEndsAt, totalMs }: { revealEndsAt: number; totalMs: number }) {
  const elapsed = useRevealElapsed(revealEndsAt, totalMs);
  const remaining = Math.max(0, Math.ceil((revealEndsAt - Date.now()) / 1000));
  const pct = 1 - elapsed / totalMs;
  const isPhase2 = elapsed >= PHASE2_START_MS;
  return (
    <div className={styles.revealCountdownWrap}>
      <span className={styles.revealCountdownLabel}>
        {isPhase2 ? t('play.revealPhase2Label') : t('play.revealPhase1Label')} - {t('play.nextCountdown', { n: remaining })}
      </span>
      <div className={styles.revealCountdownBar}>
        <div className={styles.revealCountdownFill} style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

function RevealPanel({
  revealEndsAt,
  totalMs,
  phase2StartMs,
  perPlayerResult,
  participants,
  prevParticipants,
  myLastResult,
  isSpectator,
  canonicalAnswer,
  explanation,
}: {
  revealEndsAt: number;
  totalMs: number;
  phase2StartMs: number;
  perPlayerResult: PerPlayerResult[];
  participants: LiveScoreboardEntry[];
  prevParticipants: LiveScoreboardEntry[];
  myLastResult: ReturnType<typeof gameStore.getState>['myLastResult'];
  isSpectator: boolean;
  canonicalAnswer: string;
  explanation: string | null;
}) {
  const elapsed = useRevealElapsed(revealEndsAt, totalMs);
  const hasExplanation = !!explanation;
  // Phase 1 (0→5s): đáp án + điểm + giải thích; Phase 2 (5→10s): leaderboard
  const isPhase2 = elapsed >= phase2StartMs;

  return (
    <div className={styles.revealRoot}>
      {/* Answer + my result */}
      <div className={styles.revealTop}>
        <div className={styles.revealAnswer}>
          <span className={styles.revealLabel}>{t('play.correctAnswerLabel')}</span>
          <span className={styles.revealText}>{canonicalAnswer}</span>
        </div>
        {!isSpectator && myLastResult && (
          <div className={`${styles.resultBadge} ${myLastResult.is_correct ? styles.resultCorrect : styles.resultWrong}`}>
            {myLastResult.is_correct
              ? t('play.scoreCorrect', { score: myLastResult.score_awarded.toLocaleString('vi-VN') })
              : myLastResult.score_awarded === 0 && !myLastResult.is_correct ? t('play.scoreWrong') : t('play.scoreTimeout')}
          </div>
        )}
        {!isSpectator && !myLastResult && (
          <div className={`${styles.resultBadge} ${styles.resultWrong}`}>{t('play.scoreTimeout')}</div>
        )}
      </div>

      {/* Phase 1: explanation (if available) + scores + fastest */}
      {!isPhase2 && (
        <>
          {hasExplanation && explanation && (
            <div className={styles.explanationBox}>
              <span className={styles.explanationLabel}>{t('play.explanationLabel')}</span>
              <span className={styles.explanationText}>{explanation}</span>
            </div>
          )}
          <div className={styles.phase1Grid}>
            <ScoreThisQuestion perPlayerResult={perPlayerResult} />
            <FastestResponders perPlayerResult={perPlayerResult} />
          </div>
        </>
      )}

      {/* Phase 2: animated leaderboard */}
      {isPhase2 && (
        <AnimatedLeaderboard
          participants={participants}
          prevParticipants={prevParticipants}
          perPlayerResult={perPlayerResult}
        />
      )}
    </div>
  );
}

function ScoreThisQuestion({ perPlayerResult }: { perPlayerResult: PerPlayerResult[] }) {
  const sorted = [...perPlayerResult]
    .filter((r) => r.score_awarded > 0)
    .sort((a, b) => b.score_awarded - a.score_awarded);

  return (
    <div className={styles.phase1Card}>
      <div className={styles.phase1CardTitle}>{t('play.phase1CardTitle')}</div>
      <div className={styles.phase1List}>
        {sorted.length === 0 && (
          <div className={styles.phase1Empty}>{t('play.noOneCorrect')}</div>
        )}
        {sorted.slice(0, 8).map((r, i) => (
          <ScoreRow key={r.participant_id} result={r} index={i} />
        ))}
      </div>
    </div>
  );
}

function ScoreRow({ result, index }: { result: PerPlayerResult; index: number }) {
  const displayScore = useCountUp(result.score_awarded, 1000, index * 80);
  return (
    <div className={styles.phase1Row} style={{ animationDelay: `${index * 60}ms` }}>
      <span className={styles.phase1Rank}>{index + 1}</span>
      <Avatar src={result.avatar_url ?? undefined} name={result.display_name ?? '?'} size="sm" />
      <span className={styles.phase1Name}>{result.display_name ?? t('roundControl.fallbackPlayerName')}</span>
      <span className={`${styles.phase1Score} ${styles.scoreCountUp}`}>
        +{displayScore.toLocaleString('vi-VN')}
      </span>
    </div>
  );
}

function FastestResponders({ perPlayerResult }: { perPlayerResult: PerPlayerResult[] }) {
  const sorted = [...perPlayerResult]
    .filter((r) => r.response_time_ms !== null)
    .sort((a, b) => (a.response_time_ms ?? Infinity) - (b.response_time_ms ?? Infinity));

  return (
    <div className={styles.phase1Card}>
      <div className={styles.phase1CardTitle}>{t('play.fastestTitle')}</div>
      <div className={styles.phase1List}>
        {sorted.length === 0 && (
          <div className={styles.phase1Empty}>{t('play.noOneAnswered')}</div>
        )}
        {sorted.slice(0, 8).map((r, i) => (
          <div key={r.participant_id} className={styles.phase1Row} style={{ animationDelay: `${i * 60}ms` }}>
            <span className={styles.phase1Rank}>{i + 1}</span>
            <Avatar src={r.avatar_url ?? undefined} name={r.display_name ?? '?'} size="sm" />
            <div className={styles.fastPlayerInfo}>
              <span className={styles.phase1Name}>{r.display_name ?? t('roundControl.fallbackPlayerName')}</span>
              {r.answer_text && (
                <span className={`${styles.fastAnswer} ${r.is_correct ? styles.fastAnswerCorrect : styles.fastAnswerWrong}`}>
                  "{r.answer_text}"
                </span>
              )}
            </div>
            <div className={styles.fastRow}>
              <span className={styles.fastTime}>{((r.response_time_ms ?? 0) / 1000).toFixed(1)}s</span>
              <span className={`${styles.fastBadge} ${r.is_correct ? styles.fastCorrect : styles.fastWrong}`}>
                {r.is_correct ? t('play.badgeCorrect') : t('play.badgeWrong')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnimatedLeaderboard({
  participants,
  prevParticipants,
  perPlayerResult,
}: {
  participants: LiveScoreboardEntry[];
  prevParticipants: LiveScoreboardEntry[];
  perPlayerResult: PerPlayerResult[];
}) {
  // Build prev-rank map from prevParticipants
  const prevRankMap = new Map<string, number>(
    prevParticipants.map((p, i) => [p.participant_id, p.rank ?? i + 1])
  );
  // Build score-awarded map
  const scoreMap = new Map<string, number>(
    perPlayerResult.map((r) => [r.participant_id, r.score_awarded])
  );

  return (
    <div className={styles.animLb}>
      <div className={styles.animLbTitle}>{t('play.leaderboardTitle')}</div>
      <div className={styles.animLbList}>
        {participants.map((p, i) => {
          const prevRank = prevRankMap.get(p.participant_id);
          const newRank = p.rank ?? i + 1;
          const rankDelta = prevRank != null ? prevRank - newRank : null; // positive = moved up
          const scoreAwarded = scoreMap.get(p.participant_id) ?? 0;
          const prevTotal = p.total_score - scoreAwarded;

          return (
            <div></div>
            // <AnimatedRow
            //   key={p.participant_id}
            //   participant={p}
            //   newRank={newRank}
            //   rankDelta={rankDelta}
            //   prevTotal={prevTotal}
            //   index={i}
            // />
          );
        })}
      </div>
    </div>
  );
}

function TransitionCountdown({
  startsAt,
  orderIndex,
  totalQuestions,
}: {
  startsAt: number;
  orderIndex: number;
  totalQuestions: number;
}) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.ceil((startsAt - Date.now()) / 1000)),
  );
  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(Math.max(0, Math.ceil((startsAt - Date.now()) / 1000)));
    }, 100);
    return () => clearInterval(id);
  }, [startsAt]);

  // Phát âm thanh đếm ngược 5-4-3-2-1 khi transition bắt đầu
  useEffect(() => {
    const audio = new Audio('/audio/54321.mp3');
    audio.volume = 0.7;
    audio.play().catch(() => {});
    return () => { audio.pause(); audio.currentTime = 0; };
  }, [startsAt]);

  return (
    <div className={styles.transitionWrap}>
      <div className={styles.transitionProgress}>
        {t('play.questionProgress', { n: orderIndex, total: totalQuestions })}
      </div>
      <div className={styles.transitionLabel}>{t('play.nextQuestionIn')}</div>
      <div className={styles.transitionCount}>{remaining}</div>
      <div className={styles.transitionUnit}>{t('play.nextQuestionUnit')}</div>
    </div>
  );
}

function ScrambledTiles({ text }: { text: string }) {
  // Split by whitespace — admin enters e.g. "I Ộ H N À" or "HÀ NỘI"
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  return (
    <div className={styles.tilesRow}>
      {tokens.map((token, i) => (
        <span key={i} className={styles.tile}>{token}</span>
      ))}
    </div>
  );
}

// ── Word Chain Play View ───────────────────────────────────────────────────────

// ── Word Chain Play View ───────────────────────────────────────────────────────

type WordResult = { word_index: number; correct: boolean; score_delta: number };

type CorrectParticipant = { participant_id: string; display_name: string | null; avatar_url: string | null };

type PhaseLeaderboardEntry = { rank: number; participant_id: string; display_name: string | null; avatar_url: string | null; total_score: number };

function WordChainPlayView({
  data,
  revealData,
  wordResults,
  correctByWord,
  currentWordIndex,
  phaseStartedAt,
  phaseTimeLimitSec,
  phaseLeaderboard,
  isSpectator,
  myParticipantId,
  muted,
  onMuteToggle,
  connectionStatus,
  onSubmitWord,
}: {
  data: import('@/store/gameStore').WordChainData;
  revealData: WordChainRevealData | null;
  wordResults: WordResult[];
  correctByWord: Record<number, CorrectParticipant[]>;
  currentWordIndex: number;
  phaseStartedAt: number | null;
  phaseTimeLimitSec: number;
  phaseLeaderboard: PhaseLeaderboardEntry[] | null;
  isSpectator: boolean;
  myParticipantId: string | null;
  muted: boolean;
  onMuteToggle: () => void;
  connectionStatus: string;
  onSubmitWord: (wordIndex: number, answer: string) => void;
}) {
  // hints[0] = given word (static), hints[1..] = player inputs
  const inputHints = data.chain_hints.slice(1);
  const totalInputWords = inputHints.length;

  // Local input values
  const [wordValues, setWordValues] = useState<string[]>(() => Array(totalInputWords).fill(''));
  // Server-confirmed status per word
  const [wordStatus, setWordStatus] = useState<('idle' | 'correct' | 'wrong')[]>(
    () => Array(totalInputWords).fill('idle'),
  );
  const [shakeWi, setShakeWi] = useState<number | null>(null);

  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(totalInputWords).fill(null));

  // Active word index = currentWordIndex - 1 (wi is 0-based in inputHints; wordIndex is 1-based)
  // Khi currentWordIndex = -1 mà chưa có từ nào đúng: game vừa start, phase 1 chưa đến → optimistically coi từ 0 là active.
  // Khi currentWordIndex = -1 mà đã có từ đúng: đang transition giữa các phase → không active từ nào.
  const hasCompletedWords = wordStatus.some(s => s === 'correct');
  const isTransitioning = currentWordIndex === -1 && hasCompletedWords;
  const effectiveWordIndex = currentWordIndex === -1 && !hasCompletedWords ? 1 : currentWordIndex;
  const activeWi = effectiveWordIndex >= 1 ? effectiveWordIndex - 1 : -1;

  // Sync server word results
  useEffect(() => {
    for (const r of wordResults) {
      const wi = r.word_index - 1;
      if (wi < 0 || wi >= totalInputWords) continue;
      setWordStatus(prev => {
        if (prev[wi] === 'correct') return prev;
        const next = [...prev]; next[wi] = r.correct ? 'correct' : 'wrong';
        return next;
      });
      if (!r.correct) {
        setShakeWi(wi);
        setTimeout(() => setShakeWi(null), 600);
      }
    }
  }, [wordResults, totalInputWords]);

  // Clear input + focus when a new word phase starts
  useEffect(() => {
    if (activeWi < 0 || activeWi >= totalInputWords) return;
    setWordValues(prev => { const next = [...prev]; next[activeWi] = ''; return next; });
    setWordStatus(prev => {
      const next = [...prev];
      if (next[activeWi] !== 'correct') next[activeWi] = 'idle';
      return next;
    });
    setTimeout(() => inputRefs.current[activeWi]?.focus({ preventScroll: true }), 120);
  }, [activeWi, totalInputWords]);

  // Per-phase timer (resets each time phaseStartedAt changes)
  const remainingMs = useRemainingMs(phaseStartedAt ?? 0, phaseTimeLimitSec);
  const remainingSec = phaseStartedAt ? Math.ceil(remainingMs / 1000) : phaseTimeLimitSec;
  const timerPct = phaseStartedAt ? Math.max(0, remainingMs / (phaseTimeLimitSec * 1000)) : 1;
  const urgent = remainingSec <= 10 && !!phaseStartedAt;

  const blanksFor = (wi: number) => Math.max(0, inputHints[wi].length - 1);

  const handleWordInput = (wi: number, raw: string) => {
    if (wi !== activeWi || wordStatus[wi] === 'correct') return;
    const val = raw.toUpperCase().slice(0, blanksFor(wi));
    setWordValues(prev => { const next = [...prev]; next[wi] = val; return next; });
  };

  const handleKeyDown = (wi: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (wordValues[wi].length === blanksFor(wi) && wordStatus[wi] !== 'correct') submitWord(wi);
    }
  };

  const submitWord = (wi: number) => {
    if (wordStatus[wi] === 'correct') return;
    if (wordValues[wi].length !== blanksFor(wi)) return;
    const hint = inputHints[wi];
    const answer = hint[0].toUpperCase() + wordValues[wi];
    onSubmitWord(wi + 1, answer);
  };

  const myWordCorrect = wordStatus[activeWi] === 'correct';
  const myReveal = revealData?.per_player_result.find(r => r.participant_id === myParticipantId);

  // ── REVEAL STATE ─────────────────────────────────────────────────────────────
  if (revealData) {
    return (
      <div className={styles.root}>
        {connectionStatus === 'reconnecting' && <div className={styles.connBanner}>{t('common.connecting')}</div>}
        <div className={styles.header}>
          <div className={styles.progress}>Kết quả Nối Từ</div>
          <div className={styles.headerRight}>
            <button className={styles.muteBtn} onClick={onMuteToggle}>
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
          </div>
        </div>
        <div className={styles.questionWrap}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '10px' }}>
            {revealData.correct_words.map((w, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ padding: '6px 14px', borderRadius: '8px', background: 'var(--color-primary,#1a73e8)', color: '#fff', fontWeight: 700, fontSize: '15px' }}>{w}</span>
                {i < revealData.correct_words.length - 1 && <span style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>→</span>}
              </span>
            ))}
          </div>
          {revealData.explanation && (
            <div className={styles.explanationBox}>
              <span className={styles.explanationLabel}>{t('play.explanationLabel')}</span>
              <span className={styles.explanationText}>{revealData.explanation}</span>
            </div>
          )}
        </div>
        {myReveal && !isSpectator && (
          <div className={`${styles.resultBadge} ${myReveal.score_awarded > 0 ? styles.resultCorrect : styles.resultWrong}`}
            style={{ margin: '0 auto 12px', display: 'block', textAlign: 'center' }}>
            {myReveal.score_awarded > 0 ? `+${myReveal.score_awarded.toLocaleString('vi-VN')} điểm` : 'Chưa có điểm lần này'}
          </div>
        )}
        <div className={styles.animLb}>
          <div className={styles.animLbTitle}>Bảng điểm</div>
          <div className={styles.animLbList}>
            {revealData.per_player_result.sort((a, b) => a.rank - b.rank).slice(0, 10).map((r, i) => (
              <div key={r.participant_id} className={styles.animRow} style={{ animationDelay: `${i * 60}ms` }}>
                <span className={styles.animRank}>{r.rank}</span>
                <Avatar src={r.avatar_url ?? undefined} name={r.display_name ?? '?'} size="sm" />
                <span className={styles.animName}>{r.display_name ?? 'Người chơi'}</span>
                <span className={styles.animScore}>+{r.score_awarded.toLocaleString('vi-VN')}</span>
              </div>
            ))}
          </div>
        </div>
        <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '13px', marginTop: '8px' }}>Đang chờ kết thúc màn chơi...</p>
      </div>
    );
  }

  // ── PLAY STATE ────────────────────────────────────────────────────────────────
  // Two-column layout: left = word chain, right = phase leaderboard (appears after each phase)
  // isTransitioning đã được tính ở trên (currentWordIndex === -1 && hasCompletedWords)

  return (
    <div className={styles.root}>
      {connectionStatus === 'reconnecting' && <div className={styles.connBanner}>{t('common.connecting')}</div>}
      {isSpectator && <div className={styles.spectatorBanner}>{t('play.spectatorBanner')}</div>}

      <style>{`
        @keyframes wcShake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        .wc-shake { animation: wcShake 0.5s ease; }
        @keyframes wcLbSlide { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
      `}</style>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.progress}>
          Nối Từ — Từ {effectiveWordIndex >= 1 ? effectiveWordIndex : '…'}/{totalInputWords}
        </div>
        <div className={styles.headerRight}>
          <button className={styles.muteBtn} onClick={onMuteToggle}>
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>
          {/* Per-word phase timer */}
          {!isTransitioning && phaseStartedAt && (
            <div className={styles.timerWrap}>
              <div className={`${styles.timerNum} ${urgent ? styles.timerUrgent : ''}`}>{remainingSec}</div>
              <div className={styles.timerBar}>
                <div className={`${styles.timerFill} ${urgent ? styles.timerFillUrgent : ''}`} style={{ width: `${timerPct * 100}%` }} />
              </div>
            </div>
          )}
          {isTransitioning && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', paddingRight: '4px' }}>Tiếp theo…</span>
          )}
        </div>
      </div>

      {data.question_text && (
        <div className={styles.questionWrap}>
          <p className={styles.questionText}>{data.question_text}</p>
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: '16px', width: '100%', maxWidth: '760px', margin: '0 auto', padding: '0 8px', alignItems: 'flex-start' }}>

        {/* LEFT: word chain rows */}
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Hint word (static) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'var(--color-primary,#1a73e8)', color: '#fff', fontWeight: 700, fontSize: '11px', flexShrink: 0 }}>GỢI Ý</span>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
              {data.chain_hints[0].toUpperCase().split('').map((ch, ci) => (
                <div key={ci} style={{ width: '30px', textAlign: 'center', paddingBottom: '3px', borderBottom: '2.5px solid var(--color-primary,#1a73e8)', fontSize: '20px', fontWeight: 800, color: 'var(--color-primary,#1a73e8)', userSelect: 'none' }}>{ch}</div>
              ))}
            </div>
          </div>

          {/* Input words */}
          {inputHints.map((hint, wi) => {
            const wordIdx = wi + 1; // 1-based server word index
            const status = wordStatus[wi];
            const isActive = wi === activeWi && !isTransitioning;
            const isPast = wordIdx < effectiveWordIndex || (wordIdx <= effectiveWordIndex && status === 'correct');
            const isFuture = wordIdx > effectiveWordIndex;
            const blanks = blanksFor(wi);
            const val = wordValues[wi];
            const filled = val.length === blanks;
            const borderColor = status === 'correct' ? 'var(--color-success,#28a745)'
              : status === 'wrong' ? 'var(--color-danger,#dc3545)'
              : isActive ? 'var(--color-primary,#1a73e8)'
              : 'var(--color-border,#555)';

            return (
              <div key={wi} className={shakeWi === wi ? 'wc-shake' : ''}
                style={{ position: 'relative' }}>

                {/* Nội dung từ */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

                  {/* Badge */}
                  <span style={{
                    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                    background: status === 'correct' ? 'var(--color-success,#28a745)' : isActive ? 'var(--color-primary,#1a73e8)' : 'var(--color-border,#555)',
                    color: '#fff', fontWeight: 700, fontSize: '11px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{status === 'correct' ? '✓' : wordIdx + 1}</span>

                  {/* Revealed letter + input */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flex: 1, minWidth: 0 }}>
                    <div style={{
                      width: '30px', textAlign: 'center', paddingBottom: '3px',
                      borderBottom: `2.5px solid ${borderColor}`,
                      fontSize: '20px', fontWeight: 800, color: borderColor,
                      textTransform: 'uppercase', userSelect: 'none', flexShrink: 0, lineHeight: '1.2',
                    }}>{hint[0]}</div>

                    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                      <input
                        ref={el => { inputRefs.current[wi] = el; }}
                        type="text"
                        value={val}
                        disabled={!isActive || status === 'correct'}
                        onChange={e => handleWordInput(wi, e.target.value)}
                        onKeyDown={e => handleKeyDown(wi, e)}
                        placeholder={'_'.repeat(blanks)}
                        style={{
                          width: '100%', border: 'none', outline: 'none',
                          borderBottom: `2.5px solid ${borderColor}`,
                          background: 'transparent',
                          fontSize: '20px', fontWeight: 700, letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          color: status === 'correct' ? 'var(--color-success,#28a745)' : '#ffffff',
                          paddingBottom: '3px', lineHeight: '1.2',
                          cursor: isActive ? 'text' : 'default',
                          caretColor: isActive ? 'var(--color-primary,#1a73e8)' : 'transparent',
                        }}
                        autoComplete="off" spellCheck={false}
                      />
                      {isActive && (
                        <span style={{ position: 'absolute', right: 0, bottom: '-16px', fontSize: '10px', color: 'var(--color-text-secondary)' }}>
                          {val.length}/{blanks}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Nút OK / checkmark */}
                  {!isSpectator && (
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {status === 'correct' ? (
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--color-success,#28a745)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#fff' }}>✓</div>
                      ) : isActive ? (
                        <button
                          type="button"
                          disabled={!filled}
                          onClick={() => submitWord(wi)}
                          style={{
                            padding: '6px 14px', borderRadius: '8px',
                            background: filled ? 'var(--color-primary,#1a73e8)' : 'transparent',
                            color: filled ? '#fff' : 'var(--color-border,#555)',
                            border: `1.5px solid ${filled ? 'var(--color-primary,#1a73e8)' : 'var(--color-border,#555)'}`,
                            cursor: filled ? 'pointer' : 'not-allowed',
                            fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap',
                          }}
                        >OK</button>
                      ) : null}
                      {status === 'wrong' && isActive && (
                        <span style={{ fontSize: '11px', color: 'var(--color-danger,#dc3545)', fontWeight: 600 }}>✗</span>
                      )}
                    </div>
                  )}

                  {/* Avatar người chơi trả lời đúng — ẩn khi đang hiện leaderboard */}
                  {!phaseLeaderboard && (() => {
                    const winners = correctByWord[wordIdx];
                    if (!winners || winners.length === 0) return null;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                        {winners.slice(0, 8).map((p) => (
                          <div key={p.participant_id} title={p.display_name ?? undefined} style={{ width: '20px', height: '20px', borderRadius: '50%', overflow: 'hidden', border: '1.5px solid var(--color-success,#28a745)', flexShrink: 0 }}>
                            <Avatar src={p.avatar_url ?? undefined} name={p.display_name ?? '?'} size="sm" />
                          </div>
                        ))}
                        {winners.length > 8 && (
                          <span style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginLeft: '2px' }}>+{winners.length - 8}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Mask che các từ chưa mở khóa */}
                {(isFuture || isTransitioning) && (
                  <div style={{
                    position: 'absolute', inset: '-6px -4px',
                    borderRadius: '8px',
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(3px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '6px',
                    pointerEvents: 'none',
                    transition: 'opacity 0.3s',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', fontWeight: 600, letterSpacing: '0.05em' }}>Chờ mở khóa</span>
                  </div>
                )}
              </div>
            );
          })}

          {myWordCorrect && !isSpectator && (
            <div style={{ padding: '8px 14px', borderRadius: '8px', background: 'var(--color-success-bg,#d4edda)', color: 'var(--color-success,#155724)', fontWeight: 700, fontSize: '13px' }}>
              ✓ Trả lời đúng! Đang chờ từ tiếp theo…
            </div>
          )}
          {isSpectator && (
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>{t('play.spectatorNote')}</p>
          )}
        </div>

        {/* RIGHT: phase leaderboard (appears after phase ends) */}
        {phaseLeaderboard && (
          <div style={{
            width: '200px', flexShrink: 0,
            background: 'var(--color-surface-raised,rgba(255,255,255,0.06))',
            borderRadius: '12px', padding: '12px',
            animation: 'wcLbSlide 0.3s ease',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontWeight: 700, fontSize: '13px' }}>
              <Trophy size={14} style={{ color: 'var(--color-warning,#f5a623)' }} />
              Bảng điểm
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {phaseLeaderboard.slice(0, 8).map((entry) => (
                <div key={entry.participant_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                  <span style={{ width: '16px', textAlign: 'center', fontWeight: 700, color: entry.rank <= 3 ? 'var(--color-warning,#f5a623)' : 'var(--color-text-secondary)', flexShrink: 0 }}>
                    {entry.rank}
                  </span>
                  <Avatar src={entry.avatar_url ?? undefined} name={entry.display_name ?? '?'} size="sm" />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.display_name ?? 'Người chơi'}
                  </span>
                  <span style={{ fontWeight: 700, color: 'var(--color-primary,#1a73e8)', flexShrink: 0 }}>
                    {entry.total_score.toLocaleString('vi-VN')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
